import tools from 'altrac-conversions';

const APPLICATION_VALID = [
  { name: 'None', value: 0 },
  { name: 'Schedule', value: 1 },
];

/**
 * Calculate time in seconds since equipment last started.
 * @param {Reading} reading
 * @returns {int} seconds
 */
const calculateTimeSinceStart = (reading, relay) => {
  const nowSeconds = (new Date()).getTime() / 1000;
  const startTime = relay === 1 ? reading.R1T : reading.R2T;
  const timeDiff = (nowSeconds - startTime);
  const seconds = timeDiff > 0 ? timeDiff : 0;
  return seconds;
};

export const machineState = (
  statusO,
  machineOnOff,
  machineRunningArr,
  reading0,
  deviceName,
  number = 1,
  important = true,
) => {
  let machineRunningEnabled = false;
  const machineRunning = machineRunningArr.reduce((acc, item) => {
    if (item.use === `machine${number}Feedback`) {
      machineRunningEnabled = true;
      const value = reading0[item.key];
      switch (item.type) {
        case 'closure':
          if (value - 4 > item.low) return true;
          return false;
        case 'digitalInput':
          if (value > item.low) return true;
          return false;
        case 'current4To20': {
          const mapped = tools.map(value, 4, 20, item.min, item.max);
          if (mapped > item.low) return true;
          return false;
        }
        case 'voltage0To10': {
          const mapped = tools.map(value, 0, 10, item.min, item.max);
          if (mapped > item.low) return true;
          return false;
        }
        default:
          return acc;
      }
    }
    return acc;
  }, false);
  if (machineRunningEnabled) {
    if (machineOnOff && machineRunning) {
      statusO.other.push({
        id: `pumpRunningState${number}`,
        rank: important ? 10 : 100,
        code: 'success',
        category: 'engine',
        shortMessage: 'Machine Running',
        message: `${deviceName} is running`,
      });
    } else if (!machineOnOff && machineRunning) {
      statusO.other.push({
        id: `pumpShouldBeOffState${number}`,
        rank: important ? 30 : 100,
        code: 'warning',
        category: 'engine',
        shortMessage: 'Machine Running',
        message: `${deviceName} should be off, but is running`,
      });
      statusO.deviceHeadlineWarning.enabled = true;
      statusO.deviceHeadlineWarning.text.push(`${deviceName} running in manual`);
    } else if (machineOnOff && !machineRunning) {
      statusO.other.push({
        id: `pumpShouldBeOnState${number}`,
        rank: important ? 9 : 100,
        code: 'danger',
        category: 'engine',
        shortMessage: 'Not Running',
        message: `${deviceName} should be running`,
      });
      statusO.deviceHeadlineDanger.enabled = true;
      statusO.deviceHeadlineDanger.text.unshift(`${deviceName} should be running`);

      if (calculateTimeSinceStart(reading0, number) > 300) {
        statusO.other.push({
          id: `pumpShouldBeOnState${number}Past5Min`,
          rank: 100,
          code: 'danger',
          category: 'engine',
          shortMessage: 'Not Running',
          message: `${deviceName} should be running`,
        });
      }
    } else {
      statusO.other.push({
        id: `pumpStoppedState${number}`,
        rank: important ? 100 : 100,
        code: 'normal',
        category: 'engine',
        shortMessage: 'Engine Off',
        message: `${deviceName} is off`,
      });
    }
  }
};

const autoSwitchState = (statusO, reading0, autoSwitchArr, deviceName, number) => {
  let autoDetectEnabled = false;
  const autoDetect = autoSwitchArr.reduce((acc, item) => {
    if (item.use === `autoDetectPump${number}`) {
      const value = reading0[item.key];
      autoDetectEnabled = true;
      if (!value) return false;
      return acc;
    }
    return acc;
  }, true);
  if (autoDetectEnabled) {
    if (autoDetect) {
      statusO.other.push({
        id: `autoSwitchDetect${number}On`,
        rank: 100,
        code: 'normal',
        category: 'engine',
        shortMessage: 'Auto switch detect 1 on',
        message: `Switch at ${deviceName} set to auto`,
      });
    } else {
      statusO.other.push({
        id: `autoSwitchDetect${number}Off`,
        rank: 50,
        code: 'normal',
        category: 'controller',
        shortMessage: 'A/S switch off',
        message: `Switch at ${deviceName} is not in auto position`,
      });
      statusO.stateIndicator = false;
      statusO.deviceHeadlineWarning.enabled = true;
      statusO.deviceHeadlineWarning.text.push(`Switch on ${deviceName} is not in auto position`);
    }
  }
};

export default function Pump2(status, device, _language) {
  const {
    physical: {
      deviceNumber: _deviceName,
      r1FUse = 'machine1Feedback',
      r1FType = 'closure',
      r1FMax = 16,
      r1FMin = 0,
      r1FLow = 1,
      r2FUse = 'machine2Feedback',
      r2FType = 'closure',
      r2FMax = 16,
      r2FMin = 0,
      r2FLow = 1,
      aIn1Use = 'none',
      aIn1Type = 'current4To20',
      aIn1Max = 100,
      aIn1Min = 0,
      aIn1Low = 0,
      aIn2Use = 'none',
      aIn2Type = 'current4To20',
      aIn2Max = 100,
      aIn2Min = 0,
      aIn2Low = 0,
      flowUse = 'none',
      flowType = 'flow',
      flowLow = 0,
      primaryPumpLabel = 'Primary Pump',
      secondaryPumpLabel = 'Secondary Pump',
      ioDigUse1 = 'none',
      ioDigUse2 = 'none',
      ioDigUse3 = 'none',
    },
    reading0 = {},
    interface_versioned: {
      data: {
        other: {
          page = 'pump',
        } = {},
      } = {},
    } = {},
  } = device;

  if (page !== 'pump') return;

  const {
    R1 = 0,
    R2 = 0,
    XA = 0,
    XB = 0,
  } = reading0 || {};

  const machineOnOff1 = !!R1;
  const machineOnOff2 = !!R2;
  const autoMode1 = !!XA;
  const autoMode2 = !!XB;
  const application1 = tools.isNumber(reading0['X!']) && Number(reading0['X!']) >= 0 && Number(reading0['X!']) <= 1 ? APPLICATION_VALID.find((app) => (Number(reading0['X!']) === app.value)) : APPLICATION_VALID[0];
  const application2 = tools.isNumber(reading0['X@']) && Number(reading0['X@']) >= 0 && Number(reading0['X@']) <= 1 ? APPLICATION_VALID.find((app) => (Number(reading0['X@']) === app.value)) : APPLICATION_VALID[0];

  const machineRunningArr = [
    {
      use: r1FUse, key: 'R1F', type: r1FType, max: r1FMax, min: r1FMin, low: r1FLow,
    },
    {
      use: r2FUse, key: 'R2F', type: r2FType, max: r2FMax, min: r2FMin, low: r2FLow,
    },
    {
      use: aIn1Use, key: 'AN1', type: aIn1Type, max: aIn1Max, min: aIn1Min, low: aIn1Low,
    },
    {
      use: aIn2Use, key: 'AN2', type: aIn2Type, max: aIn2Max, min: aIn2Min, low: aIn2Low,
    },
    {
      use: flowUse, key: 'PC', type: flowType, low: flowLow,
    },
    {
      use: ioDigUse1, key: 'DI1', type: 'digitalInput', low: 0,
    },
    {
      use: ioDigUse2, key: 'DI2', type: 'digitalInput', low: 0,
    },
    {
      use: ioDigUse3, key: 'DI3', type: 'digitalInput', low: 0,
    },
  ];
  // run in reverse order so warning go in correct order
  machineState(status, machineOnOff2, machineRunningArr, reading0, secondaryPumpLabel, 2, true);
  machineState(status, machineOnOff1, machineRunningArr, reading0, primaryPumpLabel, 1, true);

  const autoSwitchArr = [
    { use: ioDigUse1, key: 'DI1' },
    { use: ioDigUse2, key: 'DI2' },
    { use: ioDigUse3, key: 'DI3' },
  ];
  // run in reverse order so warning go in correct order
  autoSwitchState(status, reading0, autoSwitchArr, secondaryPumpLabel, 2);
  autoSwitchState(status, reading0, autoSwitchArr, primaryPumpLabel, 1);

  let configIssue = false;
  if (autoMode1) {
    status.other.push({
      id: 'pump1AutoMode',
      rank: 100,
      code: 'normal',
      category: 'configuration',
      shortMessage: 'Relay 1 in auto mode',
      message: `${primaryPumpLabel} in auto mode`,
    });
    status.stateIndicator = true;
    if (application1.value <= 0) {
      status.other.push({
        id: 'pump1AutoConfigurationApplicationIssue',
        rank: 100,
        code: 'normal',
        category: 'configuration',
        shortMessage: 'Relay 1 application configuration issue',
        message: `${primaryPumpLabel} will not run in auto mode because no application has been set`,
      });
      status.warningIndicator = true;
      status.deviceHeadlineWarning.enabled = true;
      status.deviceHeadlineWarning.text.push(`${primaryPumpLabel} will not run in auto mode because no application has been set`);
      configIssue = true;
    } else {
      status.other.push({
        id: 'pump1AutoConfigurationApplicationOK',
        rank: 100,
        code: 'normal',
        category: 'configuration',
        shortMessage: 'Relay 1 application configuration OK',
        message: `${primaryPumpLabel} is set to Auto and application is correctly set`,
      });
    }
  }
  if (autoMode2) {
    status.other.push({
      id: 'pump1AutoMode',
      rank: 100,
      code: 'normal',
      category: 'configuration',
      shortMessage: 'Relay 1 in auto mode',
      message: `${primaryPumpLabel} in auto mode`,
    });
    status.stateIndicator = true;
    if (application2.value <= 0) {
      status.other.push({
        id: 'pump2AutoConfigurationApplicationIssue',
        rank: 100,
        code: 'normal',
        category: 'configuration',
        shortMessage: 'Relay 2 application configuration issue',
        message: `${secondaryPumpLabel} will not run in auto mode because no application has been set`,
      });
      status.warningIndicator = true;
      status.deviceHeadlineWarning.enabled = true;
      status.deviceHeadlineWarning.text.push(`${secondaryPumpLabel} will not run in auto mode because no application has been set`);
      configIssue = true;
    } else {
      status.other.push({
        id: 'pump2AutoConfigurationApplicationOK',
        rank: 100,
        code: 'normal',
        category: 'configuration',
        shortMessage: 'Relay 2 application configuration OK',
        message: `${secondaryPumpLabel} is set to Auto and application is correctly set`,
      });
    }
  }
  if (configIssue) {
    status.stateIndicator = false;
  }

  /*

    We need to calculate status on the following:

      - Relay 1 & feedback doing what it is supposed to
      - Relay 2 & feedback doing what it is supposed to
        - What about machine state based on other inputs? analog or flow?
        - Do we need a machine state calculator for the above?

      - Digital inputs in use for HOA switches

      - Show error for misconfiguration

  */
}
